import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { User } from "../types";

export default function Users({ me, notify }: { me: User; notify: (msg: string) => void }) {
  const [users, setUsers] = useState<User[]>([]);
  const [form, setForm] = useState({ username: "", display_name: "", password: "", role: "planner" });
  const [error, setError] = useState("");

  const load = useCallback(() => api.users().then((d) => setUsers(d.users)).catch((e) => notify(e.message)), [notify]);
  useEffect(() => { load(); }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await api.createUser(form);
      setForm({ username: "", display_name: "", password: "", role: "planner" });
      notify("User created. Share the password with them securely.");
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const change = async (u: User, changes: Parameters<typeof api.updateUser>[1]) => {
    try { await api.updateUser(u.username, changes); load(); } catch (e) { notify((e as Error).message); }
  };

  const resetPassword = (u: User) => {
    const pw = window.prompt(`New password for ${u.username} (at least 10 characters)`);
    if (pw) change(u, { password: pw });
  };

  return (
    <div className="users">
      <div className="users-inner">
        <h1 style={{ margin: 0, fontSize: 20 }}>gentiK users</h1>
        <div className="panel">
          <table className="data">
            <thead><tr><th>User</th><th>Name</th><th>Role</th><th>Status</th><th /></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.username}>
                  <td className="mono">{u.username}</td>
                  <td>{u.display_name}</td>
                  <td>
                    <select className="input" style={{ width: 110 }} value={u.role} disabled={u.username === me.username}
                            onChange={(e) => change(u, { role: e.target.value })} aria-label={`Role for ${u.username}`}>
                      <option value="planner">planner</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>{u.disabled ? "Disabled" : "Active"}</td>
                  <td className="row" style={{ justifyContent: "flex-end" }}>
                    <button className="btn sm" onClick={() => resetPassword(u)}>Reset password</button>
                    {u.username !== me.username && (
                      <button className="btn sm" onClick={() => change(u, { disabled: !u.disabled })}>{u.disabled ? "Enable" : "Disable"}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form className="panel" onSubmit={create}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Add a user</h3>
          <div className="grid-2">
            <label className="field"><span>Username</span>
              <input className="input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></label>
            <label className="field"><span>Display name</span>
              <input className="input" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></label>
            <label className="field"><span>Temporary password</span>
              <input className="input" type="password" autoComplete="new-password" value={form.password}
                     onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
            <label className="field"><span>Role</span>
              <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="planner">planner</option><option value="admin">admin</option>
              </select></label>
          </div>
          {error && <div className="error-text small">{error}</div>}
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button className="btn primary" type="submit" disabled={!form.username || !form.password}>Add user</button>
          </div>
        </form>
      </div>
    </div>
  );
}
